<template>
  <lila-default-template>
    <template #mainContent>
      <div
        class="container professionals-page"
        :class="loadingContent ? 'loading large' : ''"
      >
        <h1 class="title">
          Profissionais
        </h1>

        <!-- Resumo das licenças -->
        <p class="professionals-summary">
          Licenças disponíveis:
          <lila-license-badge :licenses="licenses" />
          <span class="professionals-dot">·</span>
          Precisa de mais licenças?
          <a
            class="professionals-contact"
            href="#"
            @click.prevent="contactSales"
          >Entre em contato!</a>
        </p>

        <lila-message :alert-data="alert" />

        <!-- Filtros + ação de adicionar -->
        <div
          v-show="!loadingContent"
          class="professionals-filters"
        >
          <div class="professionals-filter professionals-filter--search">
            <label class="professionals-filter-label">
              E-mail ou nome do Profissional
            </label>
            <form @submit.prevent="applySearch">
              <lila-input
                v-model="filters.search"
                type="inputSearch"
                class="with-shadow"
                placeholder="Pesquisar"
                :btn-clear-search="filters.searchActive"
                @custom-clear-search-input-event="clearSearch"
              />
            </form>
          </div>

          <div class="professionals-filter">
            <label class="professionals-filter-label">Equipe</label>
            <div class="professionals-filter-picker-wrap">
              <button
                type="button"
                class="professionals-filter-select"
                :class="{ 'has-clear': filters.team }"
                @click.stop="filterPickerOpen = !filterPickerOpen"
              >
                <span :class="{ 'is-placeholder': !filters.team }">
                  {{ filters.team || "Selecione" }}
                </span>
                <span
                  v-if="!filters.team"
                  class="professionals-filter-caret"
                />
              </button>
              <button
                v-if="filters.team"
                type="button"
                class="professionals-filter-clear"
                aria-label="Limpar filtro de equipe"
                @click.stop="applyTeamFilter(null)"
              >
                &times;
              </button>
              <div
                v-if="filterPickerOpen"
                class="professionals-filter-picker"
              >
                <team-tree-picker
                  :tree="teamTree"
                  :is-selectable="isTeamSelectable"
                  :immediate="true"
                  :current-id="filters.teamId"
                  :show-current="false"
                  all-label="Todas as equipes"
                  title="Filtrar por equipe"
                  @select="applyTeamFilter"
                  @cancel="filterPickerOpen = false"
                />
              </div>
            </div>
          </div>

          <span class="professionals-divider" />

          <div class="professionals-filter professionals-filter--add">
            <label class="professionals-filter-label">Adicionar Profissional</label>
            <button
              type="button"
              class="professionals-add-btn"
              @click="addProfessional"
            >
              Adicionar
            </button>
          </div>
        </div>

        <!-- Barra de ação em lote (bulk-edit): alterar equipes -->
        <div
          v-if="!loadingContent && selectedIds.length > 0"
          class="professionals-bulk-bar"
        >
          <span class="professionals-bulk-count">
            {{ selectedIds.length }}
            {{
              selectedIds.length === 1
                ? "profissional selecionado"
                : "profissionais selecionados"
            }}
          </span>

          <div class="professionals-bulk-action">
            <div class="professionals-bulk-picker-wrap">
              <button
                type="button"
                class="professionals-bulk-apply"
                :disabled="bulkSaving"
                @click.stop="bulkPickerOpen = !bulkPickerOpen"
              >
                {{ bulkSaving ? "Alterando…" : "Alterar equipes" }}
              </button>
              <div
                v-if="bulkPickerOpen"
                class="professionals-bulk-picker"
              >
                <team-tree-picker
                  :tree="teamTree"
                  :is-selectable="isTeamSelectable"
                  :multiple="true"
                  :cascade-descendants="true"
                  :initial-selected="[]"
                  title="Definir equipes dos selecionados"
                  :saving="bulkSaving"
                  @select="applyBulkTeam($event)"
                  @cancel="bulkPickerOpen = false"
                />
              </div>
            </div>
          </div>

          <button
            type="button"
            class="professionals-bulk-clear"
            @click="clearSelection"
          >
            Limpar seleção
          </button>
        </div>

        <!-- Tabela -->
        <div
          v-show="!loadingContent"
          class="professionals-table-wrapper custom-scroll"
        >
          <table class="professionals-table">
            <thead>
              <tr>
                <th class="has-text-centered prof-check-col">
                  <label class="prof-check">
                    <input
                      type="checkbox"
                      :checked="allPageSelected"
                      :indeterminate.prop="somePageSelected && !allPageSelected"
                      @change="toggleSelectAllPage($event)"
                    >
                  </label>
                </th>
                <th>Nome do Profissional</th>
                <th>E-mail</th>
                <th>Equipes</th>
                <th>Status</th>
                <th class="has-text-centered">
                  Editar
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in pagedRows"
                :key="row.id"
                :class="{ 'is-selected': isSelected(row.id) }"
              >
                <td class="has-text-centered prof-check-col">
                  <label class="prof-check">
                    <input
                      type="checkbox"
                      :checked="isSelected(row.id)"
                      @change="toggleSelect(row)"
                    >
                  </label>
                </td>
                <td class="professionals-name">
                  {{ row.name }}
                </td>
                <td>{{ row.email || '—' }}</td>
                <td>
                  <div class="prof-team-cell">
                    <span
                      class="clickable-cell"
                      title="Alterar equipes"
                      @click.stop="openTeamEdit(row, $event)"
                    >
                      {{ row.teamsLabel || '—' }}
                    </span>

                    <teleport
                      v-if="teamEdit.rowId === row.id"
                      to="body"
                    >
                      <div
                        class="prof-team-picker"
                        :style="teamEdit.pos"
                      >
                        <team-tree-picker
                          :tree="teamTree"
                          :is-selectable="isTeamSelectable"
                          :multiple="true"
                          :cascade-descendants="true"
                          :initial-selected="teamEdit.ids"
                          :title="`Alterar equipes · ${row.name}`"
                          :saving="teamEdit.saving"
                          @select="saveTeamEdit(row, $event)"
                          @cancel="closeTeamEdit"
                        />
                      </div>
                    </teleport>
                  </div>
                </td>
                <td>
                  <span
                    class="professionals-status"
                    :class="row.isActive ? 'is-active' : 'is-inactive'"
                  >
                    {{ row.isActive ? 'Ativo' : 'Inativo' }}
                  </span>
                </td>
                <td class="has-text-centered">
                  <button
                    type="button"
                    class="professionals-edit-btn"
                    title="Editar"
                    @click="editRow(row)"
                  >
                    <lila-svg-icon :src="require('@assets/icons/icon_editar.svg')" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <p
            v-if="!loadingContent && filteredRows.length === 0"
            class="professionals-empty has-text-centered"
          >
            Nenhum profissional encontrado.
          </p>
        </div>

        <lila-pagination
          v-show="!loadingContent && filteredRows.length > 0"
          :pagination-config="pagination"
          @custom-change-page-event="changePage"
        />
      </div>
    </template>
  </lila-default-template>
</template>

<script>
import DefaultTemplate from "@components/template/DefaultTemplate.vue";
import Input from "@components/shared/fields/Input.vue";
import SvgIcon from "@components/shared/images/SvgIcon.vue";
import Message from "@components/shared/message/Message.vue";
import Pagination from "@components/shared/pagination/Pagination.vue";
import TeamTreePicker from "@components/shared/lila/TeamTreePicker.vue";
import LicenseBadge from "@components/shared/lila/LicenseBadge.vue";

export default {
  components: {
    "lila-default-template": DefaultTemplate,
    "lila-input": Input,
    "lila-svg-icon": SvgIcon,
    "lila-message": Message,
    "lila-pagination": Pagination,
    "team-tree-picker": TeamTreePicker,
    "lila-license-badge": LicenseBadge
  },
  data() {
    return {
      loadingContent: false,
      alert: {
        show: false,
        type: "success",
        message: ""
      },
      professionals: [],
      teams: [],
      // Unidades e equipes visíveis (api/team/available, escopadas)
      // usadas no seletor de equipes (árvore).
      transferTargets: [],
      // Edição inline de equipes (multi-seleção) por linha.
      teamEdit: {
        rowId: null,
        ids: [],
        loading: false,
        saving: false,
        pos: {}
      },
      // Bulk-edit: ids selecionados + estado do seletor/alteração em lote.
      selectedIds: [],
      bulkPickerOpen: false,
      bulkSaving: false,
      filterPickerOpen: false,
      filters: {
        search: "",
        searchActive: false,
        team: null,
        teamId: null
      },
      pagination: {
        current: 1,
        totalPages: 0,
        perPage: 10
      },
      // Resumo das licenças.
      // TODO: substituir pelos valores reais vindos do backend (plano/assinatura).
      licenses: {
        available: 0,
        total: 0,
        // Instituição sem teto de licenças (GET /api/Licenses → { unlimited: true }).
        unlimited: false
      }
    };
  },
  computed: {
    // Instituição sem teto: mostra "Ilimitado" em vez de contadores.
    // Aplica busca textual (nome/e-mail) + filtro de Equipe (lado cliente).
    filteredRows() {
      const term = this.filters.search.trim().toLowerCase();

      return this.professionals.filter((row) => {
        const matchesSearch =
          !term ||
          row.name.toLowerCase().includes(term) ||
          (row.email || "").toLowerCase().includes(term);
        const matchesTeam =
          !this.filters.team ||
          (row.teams || []).some((team) => team === this.filters.team);
        return matchesSearch && matchesTeam;
      });
    },
    // Recorte da página atual.
    pagedRows() {
      const start = (this.pagination.current - 1) * this.pagination.perPage;
      return this.filteredRows.slice(start, start + this.pagination.perPage);
    },
    // Seleção em lote (referente à página visível).
    allPageSelected() {
      return (
        this.pagedRows.length > 0 &&
        this.pagedRows.every((row) => this.selectedIds.includes(row.id))
      );
    },
    somePageSelected() {
      return this.pagedRows.some((row) => this.selectedIds.includes(row.id));
    },
    // Árvore Unidades › Equipes para o seletor, a partir das unidades e
    // equipes visíveis (api/team/available). Usa o Path (unidades ancestrais)
    // de cada item; sem Path, vira lista simples.
    //
    // O PUT api/Professional/{id} só aceita as unidades do profissional logado +
    // descendentes — o mesmo recorte do available. As unidades acima desse
    // recorte vêm só no Path e ficam como título, sem seleção (isTeamSelectable).
    teamTree() {
      const roots = [];
      const findOrAdd = (children, node) => {
        let existing = children.find((c) => String(c.id) === String(node.id));
        if (!existing) {
          existing = { id: node.id, name: node.name, children: [] };
          children.push(existing);
        }
        return existing;
      };
      this.transferTargets.forEach((target) => {
        let children = roots;
        (target.path || []).forEach((unit) => {
          children = findOrAdd(children, unit).children;
        });
        findOrAdd(children, target);
      });
      return roots;
    }
  },
  watch: {
    filteredRows() {
      this.recalculatePagination();
    }
  },
  created() {
    // Exibe a mensagem de sucesso vinda do cadastro de profissional.
    if (this.$route.query.customMessage) {
      this.$Utilities.controlAlert(
        null,
        this.alert,
        this.$route.query.customMessageType || "success",
        this.$route.query.customMessage
      );
    }
    this.getTeams();
    this.getProfessionals();
    this.getLicenses();
    this.getTransferTargets();
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
    document.addEventListener("scroll", this.closeTeamEditOnScroll, true);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
    document.removeEventListener("scroll", this.closeTeamEditOnScroll, true);
  },
  methods: {
    async getTeams() {
      try {
        const res = await this.$axios.get("api/team", {
          headers: this.authHeader()
        });
        this.teams = (res.data || []).map((item) => ({
          id: item.Id,
          name: item.Name
        }));
      } catch (error) {
        // Filtro de equipe é opcional; não bloqueia a tela.
        console.error("Erro ao carregar equipes:", error);
      }
    },

    isTeamSelectable(id) {
      return this.transferTargets.some((target) => String(target.id) === String(id));
    },

    // Unidades e equipes visíveis (escopadas por role/subárvore no backend).
    // Cada item traz o Path (unidades ancestrais) quando o backend enviar.
    async getTransferTargets() {
      try {
        const res = await this.$axios.get("api/team/available", {
          headers: this.authHeader()
        });
        const list = Array.isArray(res.data) ? res.data : res.data.Data || [];
        this.transferTargets = list.filter(Boolean).map((item) => ({
          id: this.$Utilities.getValueIgnoreCase(item, "Id"),
          name: this.$Utilities.getValueIgnoreCase(item, "Name"),
          path: (this.$Utilities.getValueIgnoreCase(item, "Path") || []).map(
            (unit) => ({
              id: this.$Utilities.getValueIgnoreCase(unit, "Id"),
              name: this.$Utilities.getValueIgnoreCase(unit, "Name")
            })
          )
        }));
      } catch (error) {
        console.error("Erro ao carregar equipes de destino:", error);
      }
    },

    async getProfessionals() {
      this.loadingContent = true;
      try {
        // Sem o filtro isActive: traz ativos e inativos (como no dashboard,
        // que mostra a coluna Status com "Ativo"/"Inativo"). O parâmetro é
        // nullable no back = sem filtro.
        const res = await this.$axios.get("/api/professional", {
          headers: this.authHeader()
        });
        const list = Array.isArray(res.data) ? res.data : res.data.Data || [];
        this.professionals = list
          .filter((item) => item != null)
          .map((item) => this.mapProfessional(item));
      } catch (error) {
        if (error.response) this.$Utilities.verifyToken(error.response.status);
        this.$Utilities.controlAlert(
          error.response ? error.response.data : null,
          this.alert,
          "danger",
          "Ocorreu um erro ao carregar os profissionais."
        );
      } finally {
        this.loadingContent = false;
        this.recalculatePagination();
      }
    },

    mapProfessional(item) {
      const teamNames = this.extractTeamNames(item);
      return {
        id: item.Id,
        name: item.Name,
        email: item.Email || "",
        teams: teamNames,
        teamsLabel: this.formatTeams(teamNames),
        // A API devolve Status ("Active"/"Inactive"/"Pending"), não IsActive.
        isActive:
          (this.$Utilities.getValueIgnoreCase(item, "Status") ?? "Active")
            .toString()
            .toLowerCase() === "active",
        // Item bruto do backend — usado na edição inline e na tela de edição
        // (não há GET api/professional/{id}; os dados já vêm na listagem).
        raw: item
      };
    },

    // Extrai a lista de nomes de equipe de um profissional, tolerando
    // diferentes formatos que o backend pode retornar (Teams como array de
    // objetos {Name}, array de strings, ou um único Team).
    extractTeamNames(item) {
      const rawTeams =
        this.$Utilities.getValueIgnoreCase(item, "Teams") ||
        this.$Utilities.getValueIgnoreCase(item, "Team");

      if (!rawTeams) return [];

      const list = Array.isArray(rawTeams) ? rawTeams : [rawTeams];
      return list
        .map((team) => {
          if (typeof team === "string") return team;
          return team && (team.Name || team.name) ? team.Name || team.name : null;
        })
        .filter((name) => !!name);
    },

    // Monta o rótulo de equipes exibido na tabela.
    // Ex.: "Suporte 1.2", "Suporte 1.1 - Suporte 1.2",
    //      "Suporte 1.1 - Suporte 1.2 e mais 3 equipe(s)".
    formatTeams(teamNames) {
      if (!teamNames || teamNames.length === 0) return "";
      const visible = teamNames.slice(0, 2).join(" - ");
      const rest = teamNames.length - 2;
      return rest > 0 ? `${visible} e mais ${rest} equipe(s)` : visible;
    },

    async getLicenses() {
      // Licenças da instituição do profissional logado.
      try {
        const res = await this.$axios.get("api/Licenses", {
          headers: this.authHeader()
        });
        const data = res.data || {};
        this.licenses.unlimited = Boolean(
          this.$Utilities.getValueIgnoreCase(data, "Unlimited")
        );
        if (this.licenses.unlimited) {
          // Sem teto de licenças: a resposta omite os números — não tratar como zero.
          this.licenses.available = Infinity;
          this.licenses.total = Infinity;
          return;
        }
        this.licenses.available = Number(
          this.$Utilities.getValueIgnoreCase(data, "AvailableLicenses") ??
            this.$Utilities.getValueIgnoreCase(data, "Available") ??
            0
        );
        this.licenses.total = Number(
          this.$Utilities.getValueIgnoreCase(data, "TotalLicenses") ??
            this.$Utilities.getValueIgnoreCase(data, "Total") ??
            0
        );
      } catch (error) {
        console.error("Erro ao carregar licenças:", error);
      }
    },

    editRow(row) {
      // Não há GET api/professional/{id}: passamos o item bruto da lista
      // para a tela de edição via sessionStorage.
      try {
        sessionStorage.setItem(
          "lila.editProfessional",
          JSON.stringify(row.raw || {})
        );
      } catch (e) {
        // sessionStorage indisponível — a tela redireciona se não achar dados.
      }
      this.$router.push({
        name: "editProfessional",
        params: { id: String(row.id) }
      });
    },

    // ---- Edição inline de equipes (multi-seleção) --------------------
    // Usa o item bruto já carregado na listagem (o PUT exige os demais
    // campos do profissional, então não podemos enviar só as equipes).
    openTeamEdit(row, event) {
      if (this.teamEdit.rowId === row.id) {
        this.closeTeamEdit();
        return;
      }
      this.teamEdit.rowId = row.id;
      this.teamEdit.saving = false;
      this.teamEdit.loading = false;
      this.teamEdit.ids = this.extractTeamIds(row.raw || {});

      // Posiciona via teleport (fixed) pra não ser cortado pelo overflow
      // horizontal da tabela — ver .professionals-table-wrapper.
      const gap = 6;
      const margin = 16;
      const popoverWidth = 420;
      const popoverHeight = 520;
      const cellRect = event.currentTarget.getBoundingClientRect();
      let left = cellRect.left;
      if (left + popoverWidth > window.innerWidth - margin) {
        left = Math.max(margin, window.innerWidth - margin - popoverWidth);
      }
      const spaceBelow = window.innerHeight - cellRect.bottom;
      const opensUp = spaceBelow < popoverHeight && cellRect.top > spaceBelow;

      this.teamEdit.pos = opensUp
        ? { left: `${left}px`, bottom: `${window.innerHeight - cellRect.top + gap}px` }
        : { left: `${left}px`, top: `${cellRect.bottom + gap}px` };
    },

    closeTeamEditOnScroll(event) {
      if (this.teamEdit.rowId == null) return;
      const target = event.target;
      if (target instanceof Element && target.closest(".prof-team-picker")) return;
      this.closeTeamEdit();
    },

    closeTeamEdit() {
      this.teamEdit.rowId = null;
      this.teamEdit.ids = [];
      this.teamEdit.saving = false;
    },

    // Ids das equipes tolerando os formatos que o backend pode retornar
    // (Teams/Doctors como array de objetos {Id} ou DoctorsId escalar).
    extractTeamIds(item) {
      const raw =
        this.$Utilities.getValueIgnoreCase(item, "Teams") ||
        this.$Utilities.getValueIgnoreCase(item, "Doctors") ||
        this.$Utilities.getValueIgnoreCase(item, "DoctorsId") ||
        [];
      const list = Array.isArray(raw) ? raw : [raw];
      return list
        .map((team) => {
          if (team == null) return null;
          if (typeof team === "object") {
            return this.$Utilities.getValueIgnoreCase(team, "Id");
          }
          return team;
        })
        .filter((id) => id != null);
    },

    // ids = array de equipes escolhidas no seletor (multi-seleção).
    async saveTeamEdit(row, ids) {
      const teamsId = Array.isArray(ids) ? ids.slice() : this.teamEdit.ids.slice();
      const raw = row.raw || {};
      const gv = (key) => this.$Utilities.getValueIgnoreCase(raw, key);

      this.teamEdit.saving = true;
      try {
        await this.$axios.put(
          `api/Professional/${row.id}`,
          {
            name: gv("Name") || row.name,
            email: gv("Email") || row.email || "",
            gender: gv("Gender") || "",
            phone: gv("Phone") || "",
            teamsId: teamsId
          },
          { headers: this.authHeader() }
        );

        // Reflete os novos nomes de equipe na linha (resolvendo pelos
        // unidades visíveis ou pela lista geral) e mantém o raw em sincronia.
        const resolveName = (id) => {
          const t = this.$Utilities.findTeamOrUnit(this.transferTargets, this.teams, id);
          return t ? t.name : null;
        };
        const selected = teamsId
          .map((id) => ({ id, name: resolveName(id) }))
          .filter((t) => t.name);
        const names = selected.map((t) => t.name);
        row.teams = names;
        row.teamsLabel = this.formatTeams(names);
        row.raw = {
          ...raw,
          Teams: selected.map((t) => ({ Id: t.id, Name: t.name }))
        };

        this.closeTeamEdit();
        this.$Utilities.controlAlert(
          null,
          this.alert,
          "success",
          "Equipes atualizadas com sucesso."
        );
      } catch (error) {
        if (error.response) this.$Utilities.verifyToken(error.response.status);
        this.$Utilities.controlAlert(
          error.response ? error.response.data : null,
          this.alert,
          "danger",
          "Não foi possível atualizar as equipes."
        );
      } finally {
        this.teamEdit.saving = false;
      }
    },

    // ---- Seleção em lote (bulk-edit) ---------------------------------
    isSelected(id) {
      return this.selectedIds.includes(id);
    },

    toggleSelect(row) {
      const index = this.selectedIds.indexOf(row.id);
      if (index === -1) this.selectedIds.push(row.id);
      else this.selectedIds.splice(index, 1);
    },

    // Marca/desmarca todos os profissionais da página visível.
    toggleSelectAllPage(event) {
      const pageIds = this.pagedRows.map((row) => row.id);
      if (event.target.checked) {
        pageIds.forEach((id) => {
          if (!this.selectedIds.includes(id)) this.selectedIds.push(id);
        });
      } else {
        this.selectedIds = this.selectedIds.filter(
          (id) => !pageIds.includes(id)
        );
      }
    },

    clearSelection() {
      this.selectedIds = [];
      this.bulkPickerOpen = false;
    },

    // Aplica as equipes escolhidas (várias) a todos os profissionais
    // selecionados. Não há endpoint de bulk no back: fazemos um
    // PUT api/Professional/{id} por profissional (o PUT exige os demais
    // campos, vindos do item bruto).
    async applyBulkTeam(ids) {
      const teamsId = Array.isArray(ids) ? ids.slice() : [];
      if (teamsId.length === 0) return;
      const targets = this.professionals.filter((p) =>
        this.selectedIds.includes(p.id)
      );
      if (targets.length === 0) return;

      this.bulkSaving = true;
      const gvOf = (raw, key) => this.$Utilities.getValueIgnoreCase(raw, key);

      const results = await Promise.allSettled(
        targets.map((row) => {
          const raw = row.raw || {};
          return this.$axios.put(
            `api/Professional/${row.id}`,
            {
              name: gvOf(raw, "Name") || row.name,
              email: gvOf(raw, "Email") || row.email || "",
              gender: gvOf(raw, "Gender") || "",
              phone: gvOf(raw, "Phone") || "",
              teamsId: teamsId
            },
            { headers: this.authHeader() }
          );
        })
      );

      // Reflete os novos nomes de equipe nas linhas que deram certo.
      const resolveName = (id) => {
        const t = this.$Utilities.findTeamOrUnit(this.transferTargets, this.teams, id);
        return t ? t.name : null;
      };
      const selectedTeams = teamsId
        .map((id) => ({ id, name: resolveName(id) }))
        .filter((t) => t.name);
      const names = selectedTeams.map((t) => t.name);

      let ok = 0;
      results.forEach((result, index) => {
        if (result.status === "fulfilled") {
          ok += 1;
          const row = targets[index];
          row.teams = names;
          row.teamsLabel = this.formatTeams(names);
          row.raw = {
            ...(row.raw || {}),
            Teams: selectedTeams.map((t) => ({ Id: t.id, Name: t.name }))
          };
        }
      });

      this.bulkSaving = false;
      this.bulkPickerOpen = false;

      const failed = targets.length - ok;
      if (failed === 0) {
        this.clearSelection();
        this.$Utilities.controlAlert(
          null,
          this.alert,
          "success",
          `Equipes atualizadas para ${ok} profissional(is).`
        );
      } else {
        this.$Utilities.controlAlert(
          null,
          this.alert,
          "danger",
          `${ok} atualizado(s), ${failed} com falha. Tente novamente os restantes.`
        );
      }
    },

    // Filtro de Equipe via o mesmo dropdown em árvore (immediate). O picker
    // devolve o id; guardamos o nome em filters.team (o matching é por nome).
    applyTeamFilter(id) {
      if (id == null) {
        this.filters.team = null;
        this.filters.teamId = null;
      } else {
        const t = this.$Utilities.findTeamOrUnit(this.transferTargets, this.teams, id);
        this.filters.team = t ? t.name : null;
        this.filters.teamId = t ? id : null;
      }
      this.filterPickerOpen = false;
      this.applyClientFilters();
    },

    handleClickOutside(event) {
      // Fecha o seletor de filtro de equipe.
      if (this.filterPickerOpen) {
        let inside = false;
        document
          .querySelectorAll(".professionals-filter-picker")
          .forEach((picker) => {
            if (picker.contains(event.target)) inside = true;
          });
        if (!inside) this.filterPickerOpen = false;
      }

      // Fecha o seletor inline de equipes de uma linha.
      if (this.teamEdit.rowId != null) {
        let inside = false;
        document.querySelectorAll(".prof-team-picker").forEach((picker) => {
          if (picker.contains(event.target)) inside = true;
        });
        if (!inside && !event.target.classList.contains("clickable-cell")) {
          this.closeTeamEdit();
        }
      }

      // Fecha o seletor de equipes do bulk-edit.
      if (this.bulkPickerOpen) {
        let inside = false;
        document
          .querySelectorAll(".professionals-bulk-picker")
          .forEach((picker) => {
            if (picker.contains(event.target)) inside = true;
          });
        if (!inside) this.bulkPickerOpen = false;
      }
    },

    addProfessional() {
      this.$router.push({ name: "addProfessional" });
    },

    contactSales() {
      this.$Utilities.controlAlert(
        null,
        this.alert,
        "success",
        "Para adquirir mais licenças, entre em contato com o suporte da Lila Saúde."
      );
    },

    applySearch() {
      this.filters.searchActive = this.filters.search.trim() !== "";
      this.pagination.current = 1;
    },

    clearSearch() {
      this.filters.search = "";
      this.filters.searchActive = false;
      this.pagination.current = 1;
    },

    applyClientFilters() {
      this.pagination.current = 1;
    },

    changePage(page) {
      this.pagination.current = page;
    },

    recalculatePagination() {
      const total = Math.ceil(this.filteredRows.length / this.pagination.perPage);
      this.pagination.totalPages = total;
      if (this.pagination.current > total) {
        this.pagination.current = total || 1;
      }
      if (this.pagination.current < 1) {
        this.pagination.current = 1;
      }
    },

    authHeader() {
      return {
        Authorization: `bearer ${
          this.$Session.get(this.$userSessionName).token.Value
        }`
      };
    }
  }
};
</script>

<style lang="scss">
$lila-primary: #4f3f82;
$lila-accent: #9a397d;

.container.professionals-page {
  max-width: none;
  width: 100%;
}

.professionals-page {
  padding: 10px 24px 0;
}

.professionals-page .title {
  color: $lila-primary;
  font-weight: 700;
  margin-bottom: 10px;
}

/* Resumo de licenças */
.professionals-page .professionals-summary {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  color: #6b6b6b;
  font-size: 14px;
  margin-bottom: 26px;
}

.professionals-page .professionals-dot {
  color: #c7c7c7;
}

.professionals-page .professionals-contact {
  color: $lila-accent;
  font-weight: 700;
  text-decoration: none;
}

.professionals-page .professionals-contact:hover {
  text-decoration: underline;
}

/* Filtros */
.professionals-page .professionals-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 20px;
  margin-bottom: 24px;
}

.professionals-page .professionals-filter {
  flex: 0 1 320px;
  min-width: 240px;
}

.professionals-page .professionals-filter--search {
  flex: 0 1 420px;
}

.professionals-page .professionals-filter--add {
  flex: 0 0 auto;
  min-width: 0;
}

.professionals-page .professionals-filter-label {
  display: block;
  font-size: 13px;
  color: #6b6b6b;
  margin-bottom: 8px;
}

/* Divisória vertical antes do botão adicionar */
.professionals-page .professionals-divider {
  align-self: stretch;
  width: 1px;
  background: #e4e1ee;
  margin: 0 4px 0 auto;
}

/* Botão "Adicionar Profissional" */
.professionals-page .professionals-add-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: $lila-accent;
  color: #ffffff;
  border: none;
  border-radius: 999px;
  padding: 11px 26px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s ease;
}

.professionals-page .professionals-add-btn:hover {
  background: darken($lila-accent, 6%);
}

/* Tabela — cada linha é um "card" separado, com espaçamento entre elas */
.professionals-page .professionals-table-wrapper {
  overflow-x: auto;
}

.professionals-page .professionals-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 12px;
}

.professionals-page .professionals-table thead th {
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: #3a3a3a;
  padding: 6px 24px;
  white-space: nowrap;
  border-bottom: none;
}

.professionals-page .professionals-table tbody tr {
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(79, 63, 130, 0.06);
  transition: box-shadow 0.2s ease;
}

.professionals-page .professionals-table tbody tr:hover {
  box-shadow: 0 4px 14px rgba(79, 63, 130, 0.12);
}

.professionals-page .professionals-table tbody td {
  padding: 22px 24px;
  font-size: 14px;
  color: #8a8a8a;
}

/* Cantos arredondados nas extremidades de cada linha-card */
.professionals-page .professionals-table tbody td:first-child {
  border-top-left-radius: 12px;
  border-bottom-left-radius: 12px;
}

.professionals-page .professionals-table tbody td:last-child {
  border-top-right-radius: 12px;
  border-bottom-right-radius: 12px;
}

.professionals-page .professionals-table .professionals-name {
  color: #3a3a3a;
  font-weight: 400;
}

/* Status */
.professionals-page .professionals-status {
  font-weight: 600;
}

.professionals-page .professionals-status.is-active {
  color: #3a3a3a;
}

.professionals-page .professionals-status.is-inactive {
  color: #b0b0b0;
}

/* Botão editar */
.professionals-page .professionals-edit-btn {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 4px;
  line-height: 0;
}

.professionals-page .professionals-edit-btn svg,
.professionals-page .professionals-edit-btn img {
  width: 18px;
  height: 18px;
}

/* O SvgIcon tem @click próprio com stopPropagation; se o clique cair em
   qualquer descendente (div wrapper, <svg>, <path>...) ele é engolido antes
   de chegar no <button>, exigindo um segundo clique. Neutralizar TUDO dentro
   do botão pra que só o próprio <button> receba o evento. */
.professionals-page .professionals-edit-btn * {
  pointer-events: none;
}

.professionals-page .professionals-empty {
  padding: 30px 0;
  color: #9a9a9a;
}

/* Seleção em lote (checkbox) */
.professionals-page .prof-check-col {
  width: 44px;
}

.professionals-page .prof-check {
  display: inline-flex;
  cursor: pointer;
}

.professionals-page .prof-check input {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: $lila-primary;
}

.professionals-page .professionals-table tbody tr.is-selected td {
  background-color: rgba(79, 63, 130, 0.06);
}

/* Barra de ação em lote */
.professionals-page .professionals-bulk-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 14px;
  padding: 12px 16px;
  background-color: #f4f2fa;
  border: 1px solid #e2ddf0;
  border-radius: 10px;
}

.professionals-page .professionals-bulk-count {
  font-weight: 600;
  color: $lila-primary;
}

.professionals-page .professionals-bulk-action {
  display: flex;
  align-items: center;
  gap: 10px;
}

.professionals-page .professionals-bulk-action .select-component {
  margin: 0;
  min-width: 200px;
}

/* Popover do seletor de equipes (multi) do bulk-edit */
.professionals-page .professionals-bulk-picker-wrap {
  position: relative;
}

.professionals-page .professionals-bulk-picker {
  position: absolute;
  top: 0;
  left: calc(100% + 8px);
  z-index: 600;
}

/* Filtro de Equipe: botão-gatilho no estilo dos campos + popover do picker */
.professionals-page .professionals-filter-picker-wrap {
  position: relative;
}

.professionals-page .professionals-filter-select {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  background: #ffffff;
  border: none;
  border-radius: 999px;
  box-shadow: 0 2px 6px rgba(79, 63, 130, 0.1);
  padding: 11px 20px;
  font-size: 14px;
  color: #3a3a3a;
  cursor: pointer;
  font-family: inherit;
}

.professionals-page .professionals-filter-select > span:first-child {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.professionals-page .professionals-filter-select .is-placeholder {
  color: #9a95a8;
}

.professionals-page .professionals-filter-caret {
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 6px solid $lila-accent;
  flex: none;
}

.professionals-page .professionals-filter-select.has-clear {
  padding-right: 48px;
}

.professionals-page .professionals-filter-clear {
  position: absolute;
  top: 50%;
  right: 14px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0 0 2px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: $lila-accent;
  font-size: 20px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
}

.professionals-page .professionals-filter-clear:hover {
  opacity: 0.7;
}

.professionals-page .professionals-filter-picker {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 600;
}

.professionals-page .professionals-bulk-apply {
  border: none;
  border-radius: 8px;
  padding: 9px 18px;
  background-color: $lila-primary;
  color: #ffffff;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s ease;
}

.professionals-page .professionals-bulk-apply:hover:not(:disabled) {
  opacity: 0.9;
}

.professionals-page .professionals-bulk-apply:disabled {
  opacity: 0.5;
  cursor: default;
}

.professionals-page .professionals-bulk-clear {
  border: none;
  background: transparent;
  color: #6b6b6b;
  cursor: pointer;
  text-decoration: underline;
  margin-left: auto;
}

/* Edição inline de equipes (multi-seleção) */
.professionals-page .prof-team-cell {
  position: relative;
  display: inline-block;
}

.professionals-page .clickable-cell {
  cursor: pointer;
  color: inherit;
}

.professionals-page .clickable-cell:hover {
  text-decoration: underline;
  color: $lila-accent;
}

/* Popover do seletor de equipes em árvore. Teleportado pro <body> (via
   <teleport>) e posicionado via position:fixed com top/left calculados em
   openTeamEdit() (getBoundingClientRect) — assim ele nunca é cortado pelo
   overflow-x:auto da tabela, independente da altura/posição da linha. */
.prof-team-picker {
  position: fixed;
  z-index: 600;
}
</style>
